import type { IsoDateStr } from '@moment/domain-types'

import { parseIsoDate } from '@moment/utils/datetime'

import { AccountBarChartTimePeriod } from '@/types/enums/accounts'
import { type Cashflow, CashflowType } from '@/types/statistics'

export const timeRangeOptions = [
  { label: 'Annual', value: AccountBarChartTimePeriod.Annual },
  { label: 'Monthly', value: AccountBarChartTimePeriod.Monthly },
]

// A type alias (not interface) so it satisfies BarChart's Record<string, unknown> data rows.
export type AggregatedCashflow = {
  date: string
  coupon: number
  principal: number
  call: number
  dividend: number
}

/**
 * Aggregates cashflows into a format suitable for a stacked bar chart.
 *
 * @param cashflows Array of Cashflow objects.
 * @returns Aggregated cashflows with coupon, principal, call, and dividend amounts per date.
 */
export function aggregateCashflows(
  cashflows: Cashflow[] | undefined,
): AggregatedCashflow[] {
  if (!cashflows) return []

  const aggregatedMap: Record<
    string,
    { coupon: number; principal: number; call: number; dividend: number }
  > = {}

  cashflows.forEach((cashflow) => {
    const { date, amount, type } = cashflow

    if (!aggregatedMap[date]) {
      aggregatedMap[date] = { coupon: 0, principal: 0, call: 0, dividend: 0 }
    }

    if (type === CashflowType.Coupon) {
      aggregatedMap[date].coupon += amount
    } else if (type === CashflowType.Principal) {
      aggregatedMap[date].principal += amount
    } else if (type === CashflowType.Call) {
      aggregatedMap[date].call += amount
    } else if (type === CashflowType.Dividend) {
      aggregatedMap[date].dividend += amount
    }
  })

  return Object.entries(aggregatedMap).map(
    ([date, { coupon, principal, call, dividend }]) => ({
      date,
      coupon,
      principal,
      call,
      dividend,
    }),
  )
}

interface CashflowsByPeriod {
  monthly_cashflows: Cashflow[]
  yearly_cashflows: Cashflow[]
}

/** True when any period has a call payment, so charts can hide the Call
 *  series and legend entry for accounts without callable bonds. */
export function hasCallCashflows(cashflows: CashflowsByPeriod): boolean {
  return cashflows.monthly_cashflows
    .concat(cashflows.yearly_cashflows)
    .some((cf) => cf.type === CashflowType.Call && cf.amount > 0)
}

/** The toggle hands back a string; map it to the period it names. */
export function parseTimeRange(
  value: string,
): AccountBarChartTimePeriod | undefined {
  return timeRangeOptions.find((option) => option.value === value)?.value
}

// An annual chart of one or two bars says little; months show the shape.
const MAX_YEARS_FOR_MONTHLY_DEFAULT = 2

interface DatedCashflowsByPeriod {
  monthly_cashflows: readonly unknown[]
  yearly_cashflows: ReadonlyArray<{ date: IsoDateStr }>
}

/** Monthly when there is a monthly series and the annual one spans too few years to be informative. */
export function defaultCashflowPeriod(
  cashflows: DatedCashflowsByPeriod,
): AccountBarChartTimePeriod {
  if (cashflows.monthly_cashflows.length === 0) {
    return AccountBarChartTimePeriod.Annual
  }
  const years = new Set(
    cashflows.yearly_cashflows.map(
      (cashflow) => parseIsoDate(cashflow.date).year,
    ),
  )
  return years.size <= MAX_YEARS_FOR_MONTHLY_DEFAULT
    ? AccountBarChartTimePeriod.Monthly
    : AccountBarChartTimePeriod.Annual
}
